import React from "react";
import { MaterialReferenceManager } from "./MaterialReferenceManager";
import { AlertTriangle, CheckCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { checkManufacturingSchema } from "@/lib/api/manufacturing";
import { ManufacturingProductManager } from "@/components/manufacturing/ManufacturingProductManager";
import { MaterialsProcesses } from "@/components/manufacturing/MaterialsProcesses";
import { ManufacturingSimulator } from "@/components/manufacturing/ManufacturingSimulator";
import { ProductionOrdersWorkspace } from "@/components/manufacturing/ProductionOrdersWorkspace";
import { SupplierReadOnly } from "@/components/manufacturing/SupplierReadOnly";
import { TemplateManager } from "@/components/manufacturing/TemplateManager";

const viewConfig = {
  articles: {
    title: "Artículos",
    description: "Catálogo de artículos de fabricación y sincronización con artículos comerciales.",
  },
  simulations: {
    title: "Simulaciones de órdenes de fabricación",
    description: "Estudios de coste previos a fabricar. Puedes convertir una simulación en orden real.",
  },
  orders: {
    title: "Órdenes de fabricación",
    description: "Gestiona las órdenes reales de fabricación con suborden por artículo.",
  },
  families: {
    title: "Maestros · Familias",
    description: "Mantenimiento de familias legacy asociadas a artículos de fabricación.",
  },
  "garment-types": { title: "Maestros · Tipos de prenda", description: "Clasifica los artículos como pantalones, camisas, vestidos y otros tipos de prenda." },
  seasons: {
    title: "Maestros · Temporadas",
    description: "Mantenimiento de temporadas legacy asociadas a artículos de fabricación.",
  },
  processes: {
    title: "Maestros · Procesos",
    description: "Mantenimiento de procesos productivos reutilizables en escandallos.",
  },
  "material-references": { title: "Maestros · Referencias de materiales", description: "Gestiona las referencias de proveedor y depura el catálogo de materiales." },
  materials: {
    title: "Maestros · Materiales",
    description: "Mantenimiento de materiales, unidades y proveedores asociados.",
  },
  templates: {
    title: "Maestros · Plantillas",
    description: "Plantillas de procesos y materiales para construir escandallos de forma repetible.",
  },
  suppliers: {
    title: "Maestros · Proveedores",
    description: "Listado y búsqueda de proveedores importados desde el ERP.",
  },
  "supplier-types": {
    title: "Maestros · Tipos de proveedores",
    description: "Listado y búsqueda de tipos de proveedor importados desde el ERP.",
  },
};

export function ManufacturingDashboard({ view = "articles" }) {
  const [schemaStatus, setSchemaStatus] = React.useState({ loading: true, ok: false, error: null });
  const { toast } = useToast();
  const currentView = viewConfig[view] ? view : "articles";
  const currentConfig = viewConfig[currentView];

  const verifySchema = React.useCallback(async () => {
    setSchemaStatus({ loading: true, ok: false, error: null });
    const result = await checkManufacturingSchema();
    setSchemaStatus({ loading: false, ok: result.ok, error: result.error });
    if (!result.ok) {
      toast({
        title: "Producción no disponible",
        description: result.error?.message || "No se pudo validar el esquema mfg_*.",
        variant: "destructive",
      });
    }
  }, [toast]);

  React.useEffect(() => {
    verifySchema();
  }, [verifySchema]);

  const renderView = () => {
    switch (currentView) {
      case "simulations":
        return (
          <ManufacturingSimulator
            mode="group"
            onProductionOrderCreated={() => {
              window.location.hash = "#/ordenes-fabricacion";
            }}
          />
        );
      case "orders":
        return <ProductionOrdersWorkspace />;
      case "families":
        return <MaterialsProcesses initialTab="families" showNavigation={false} />;
      case "garment-types":
        return <MaterialsProcesses initialTab="garment-types" showNavigation={false} />;
      case "seasons":
        return <MaterialsProcesses initialTab="seasons" showNavigation={false} />;
      case "processes":
        return <MaterialsProcesses initialTab="processes" showNavigation={false} />;
      case "material-references":
        return <MaterialReferenceManager />;
      case "materials":
        return <MaterialsProcesses initialTab="materials" showNavigation={false} />;
      case "templates":
        return <TemplateManager />;
      case "suppliers":
        return <SupplierReadOnly mode="suppliers" />;
      case "supplier-types":
        return <SupplierReadOnly mode="supplier-types" />;
      case "articles":
      default:
        return <ManufacturingProductManager />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{currentConfig.title}</h1>
            <p className="text-sm text-gray-600">{currentConfig.description}</p>
          </div>
          <Button variant="outline" onClick={verifySchema} disabled={schemaStatus.loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Verificar BD
          </Button>
        </div>

        <div className={`mt-4 flex items-start gap-2 rounded border p-3 text-sm ${schemaStatus.ok ? "border-green-200 bg-green-50 text-green-800" : "border-yellow-200 bg-yellow-50 text-yellow-900"}`}>
          {schemaStatus.ok ? <CheckCircle className="mt-0.5 h-4 w-4" /> : <AlertTriangle className="mt-0.5 h-4 w-4" />}
          <div>
            {schemaStatus.loading
              ? "Comprobando acceso a las tablas de producción..."
              : schemaStatus.ok
                ? "Esquema de escandallos accesible para el usuario admin actual."
                : `No se pudo comprobar el esquema: ${schemaStatus.error?.message || "error desconocido"}`}
          </div>
        </div>
      </div>

      {renderView()}
    </div>
  );
}
